import { useState, useEffect } from 'react';
import { getAllFamilyPersons, getPerson, fetchFamilyTreeData } from '../services/genealogyAPI';

export const useGenealogyData = (rootPersonId = null, generations = 5) => {
  const [familyTree, setFamilyTree] = useState(null);
  const [allPersons, setAllPersons] = useState([]);
  const [rootPerson, setRootPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all family persons first
  useEffect(() => {
    const fetchAllPersons = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const persons = await getAllFamilyPersons();
        setAllPersons(persons);
        
        // If no root person specified, use the first person as root
        if (!rootPersonId && persons.length > 0) {
          const firstPerson = persons[0];
          setRootPerson(firstPerson);
          return firstPerson.id;
        }
        
        return rootPersonId;
      } catch (err) {
        console.error('Error fetching all persons:', err);
        setError('Failed to load family members');
        setLoading(false);
        return null;
      }
    };

    fetchAllPersons();
  }, [rootPersonId]);

  // Fetch family tree data when root person is available
  useEffect(() => {
    const fetchFamilyTree = async () => {
      if (!rootPerson && !rootPersonId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const currentRootId = rootPersonId || rootPerson?.id;
        if (!currentRootId) return;
        
        // Use the enhanced balkan_tree endpoint with generation mapping
        const treeData = await fetchFamilyTreeData();
        setFamilyTree(treeData);
        
        // If we don't have root person yet, fetch it
        if (!rootPerson) {
          const person = await getPerson(currentRootId);
          setRootPerson(person);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching family tree:', err);
        setError('Failed to load family tree');
        setLoading(false);
      }
    };

    fetchFamilyTree();
  }, [rootPerson, rootPersonId, generations]);

  // Function to refresh data
  const refreshData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const currentRootId = rootPersonId || rootPerson?.id;
      if (currentRootId) {
        const [treeData, persons] = await Promise.all([
          fetchFamilyTreeData(),
          getAllFamilyPersons()
        ]);
        
        setFamilyTree(treeData);
        setAllPersons(persons);
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  // Function to change root person
  const changeRootPerson = async (newRootPersonId) => {
    try {
      setLoading(true);
      setError(null);
      
      const person = await getPerson(newRootPersonId);
      const treeData = await fetchFamilyTreeData();
      
      setRootPerson(person);
      setFamilyTree(treeData);
    } catch (error) {
      console.error('Error changing root person:', error);
      setError('Failed to change root person');
    } finally {
      setLoading(false);
    }
  };

  // Function to load more generations
  const loadMoreGenerations = async (additionalGenerations = 2) => {
    if (!rootPerson) return;
    
    try {
      setLoading(true);
      const newGenerations = generations + additionalGenerations;
      const treeData = await fetchFamilyTreeData();
      
      setFamilyTree(treeData);
      return newGenerations;
    } catch (err) {
      console.error('Error loading more generations:', err);
      setError('Failed to load more generations');
    } finally {
      setLoading(false);
    }
  };

  return {
    familyTree,
    allPersons,
    rootPerson,
    loading,
    error,
    refreshData,
    changeRootPerson,
    loadMoreGenerations
  };
};

// Hook for managing a single person
export const usePerson = (personId) => {
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPerson = async () => {
      if (!personId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const personData = await getPerson(personId);
        setPerson(personData);
      } catch (err) {
        console.error('Error fetching person:', err);
        setError('Failed to load person data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerson();
  }, [personId]);

  return { person, loading, error };
};
